/**
 * One muted line that spells out what a rating scale actually means.
 *
 * Wording mirrors `ratingGuidance` in the questionnaire compiler
 * (apps/backend/src/questionnaires/compiler/service.ts) so the report page and
 * the deck read the same: `1 means "I hate it" and 5 means "I love it"`.
 *
 * Multi-measure exhibits prefix each distinct scale with the measure labels it
 * covers and join them with a middot, exactly like the compiler groups its
 * per-measure guidance. Measures that share a scale collapse into one segment,
 * and the prefix is dropped only when every measure carried anchors and they
 * all agreed. A measure skipped for missing anchors is not covered by the
 * surviving measure's scale, so the prefix stays to say who the line is about.
 *
 * Returns null when no segment carries any anchor text. Callers rely on that to
 * keep whatever legend they already render (synthesized share matrices carry no
 * anchors by construction and must keep their generic swatch strip).
 */

export interface ScaleLegendSegment {
  measureLabel?: string | null;
  min: number;
  max: number;
  minLabel?: string | null;
  maxLabel?: string | null;
}

export interface ScaleLegendFallback {
  minLabel: string;
  maxLabel: string;
}

/** Mirrors the compiler's `anchors?.minLabel ?? 'low'` half-anchor fill-in. */
const DEFAULT_FALLBACK: ScaleLegendFallback = {
  minLabel: "low",
  maxLabel: "high",
};

const SEGMENT_SEPARATOR = " · ";

interface ScaleLegendGroup {
  body: string;
  measureLabels: Array<string>;
}

function formatBound(value: number): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "";
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(1);
}

function normalizeLabel(label?: string | null): string {
  return (label || "").replace(/\s+/g, " ").trim();
}

function segmentBody(
  segment: ScaleLegendSegment,
  fallback: ScaleLegendFallback,
): string | null {
  const minLabel = normalizeLabel(segment.minLabel);
  const maxLabel = normalizeLabel(segment.maxLabel);
  if (!minLabel && !maxLabel) {
    return null;
  }

  const min = formatBound(segment.min);
  const max = formatBound(segment.max);
  if (!min || !max) {
    return null;
  }

  return `${min} means "${minLabel || fallback.minLabel}" and ${max} means "${maxLabel || fallback.maxLabel}"`;
}

export interface ScaleLegendResult {
  /** The rendered line, or null when no segment carried any anchor text. */
  text: string | null;
  /**
   * True when every segment carried anchors and they all agreed, so the line
   * collapsed to one unprefixed segment that speaks for the whole exhibit.
   * Callers use this to decide whether a shared piece of chrome (the swatch
   * strip) may borrow the same wording.
   */
  collapsed: boolean;
}

export function buildScaleLegendResult(
  segments: Array<ScaleLegendSegment>,
  fallback: ScaleLegendFallback = DEFAULT_FALLBACK,
): ScaleLegendResult {
  const groups: Array<ScaleLegendGroup> = [];
  let skippedForMissingAnchors = false;

  for (const segment of segments || []) {
    const body = segmentBody(segment, fallback);
    if (!body) {
      skippedForMissingAnchors = true;
      continue;
    }

    const measureLabel = normalizeLabel(segment.measureLabel);
    const existing = groups.find((group) => group.body === body);
    if (existing) {
      if (measureLabel && !existing.measureLabels.includes(measureLabel)) {
        existing.measureLabels.push(measureLabel);
      }
      continue;
    }

    groups.push({
      body,
      measureLabels: measureLabel ? [measureLabel] : [],
    });
  }

  if (groups.length === 0) {
    return { text: null, collapsed: false };
  }

  // A single surviving group only speaks for the whole exhibit when nothing was
  // left out. If a measure was skipped for missing anchors, dropping the prefix
  // would read as if the anchored measure's scale covered that sibling too.
  const collapsed = groups.length === 1 && !skippedForMissingAnchors;
  if (collapsed) {
    return { text: groups[0].body, collapsed: true };
  }

  const text = groups
    .map((group) => (
      group.measureLabels.length
        ? `${group.measureLabels.join(", ")}: ${group.body}`
        : group.body
    ))
    .join(SEGMENT_SEPARATOR);

  return { text, collapsed: false };
}

export function buildScaleLegend(
  segments: Array<ScaleLegendSegment>,
  fallback: ScaleLegendFallback = DEFAULT_FALLBACK,
): string | null {
  return buildScaleLegendResult(segments, fallback).text;
}
